import { MessageSquare, Loader2, Trash2 } from "lucide-react";

import { DEFAULT_CHAT_TITLE } from "@/components/VibeChat/constants";
import { Button } from "@/components/ui/button";
import { DropdownMenuItem } from "@/components/ui/dropdown-menu";
import { OverflowDisplayTooltip } from "@/components/ui/Tooltip/OverflowDisplayTooltip";
import { useUser } from "@/context/user-context";
import { ChatSession as APIChatSession } from "@/queries/vibe/useRecentVibeChatSessions";
import { cn } from "@/lib/utils";
import { formatCompactRelativeTime } from "@/utils/timestamp";

interface ChatSessionItemProps {
  chat: APIChatSession;
  isActive: boolean;
  isStreaming?: boolean;
  onSelect: () => void;
  onDelete?: (chatId: string) => void;
  displayName?: string;
}

export const ChatSessionItem = ({
  chat,
  isActive,
  isStreaming = false,
  onSelect,
  onDelete,
  displayName,
}: ChatSessionItemProps) => {
  const { workspaces } = useUser();
  const workspaceName = workspaces.find(workspace => workspace.id === chat.workspace_id)?.name;
  const lastUsed = formatCompactRelativeTime(chat.updated_at || chat.created_at);
  const chatTitle = displayName ?? chat.name ?? DEFAULT_CHAT_TITLE;

  return (
    <DropdownMenuItem
      onSelect={onSelect}
      className={cn(
        "group relative flex w-full items-center gap-2.5 rounded-md px-3 py-2 text-sm transition-[background-color,padding] duration-150",
        isActive
          ? "bg-gray-100 text-gray-900 hover:bg-gray-100 focus:bg-gray-100 data-[highlighted]:bg-gray-100"
          : "text-gray-700 hover:bg-gray-50 focus:bg-gray-50 data-[highlighted]:bg-gray-50",
        onDelete && "focus-within:pr-9 hover:pr-9 data-[highlighted]:pr-9",
      )}
    >
      <div
        className={cn(
          "flex size-6 flex-shrink-0 items-center justify-center rounded-md",
          isActive ? "bg-gray-200" : "bg-gray-100",
        )}
      >
        {chat.isGeneratingName || isStreaming ? (
          <Loader2 className="size-3 animate-spin text-gray-500" />
        ) : (
          <MessageSquare className="size-3 text-gray-500" />
        )}
      </div>

      <div className="min-w-0 flex-1">
        <OverflowDisplayTooltip content={chatTitle} side="top">
          <span className="font-medium">{chatTitle}</span>
        </OverflowDisplayTooltip>
        {(workspaceName || lastUsed) && (
          <div
            className={cn(
              "flex items-center gap-1 text-[11px]",
              isActive ? "text-gray-500" : "text-gray-400",
            )}
          >
            {workspaceName && <span className="truncate">{workspaceName}</span>}
            {workspaceName && lastUsed && <span aria-hidden>·</span>}
            {lastUsed && <span className="shrink-0 tabular-nums">{lastUsed}</span>}
          </div>
        )}
      </div>
      {onDelete && (
        <Button
          variant="ghost"
          size="icon"
          type="button"
          onClick={e => {
            e.stopPropagation();
            e.preventDefault();
            onDelete(chat.id);
          }}
          className="absolute right-1.5 top-1/2 size-7 -translate-y-1/2 text-red-500 opacity-0 transition-opacity hover:bg-red-50 hover:text-red-600 group-focus-within:opacity-100 group-hover:opacity-100 group-data-[highlighted]:opacity-100"
        >
          <Trash2 className="size-3.5" />
        </Button>
      )}
    </DropdownMenuItem>
  );
};
